import type {
  User,
  UserPreferences,
  OIAlertsStatus,
  AlertTrigger,
  AlertSnapshot,
  Alert,
  OrderbookStatus,
  OrderbookAlert,
  TrackedOrder,
  MarkPriceStatus,
  MarkPriceAlert,
  MarkPriceTrigger,
  FundingStatus,
  FundingAlert,
  PriceDifferenceStatus,
  PriceDifferenceSettings,
  PriceDifferenceAlert,
  SplashStatus,
  SplashSettings,
  SplashAlert,
  SpreadStatus,
  SpreadSettings,
  SpreadAlert,
  Aliases,
  Position,
  PositionUser,
  CreatePositionPayload,
  UpdatePositionPayload,
  ReopenPositionPayload,
  StateStatusResponse,
  StateClearResponse,
  MarketRecord,
  ScreenerTicker,
  ScreenerSettings,
  ScreenerTickerData,
  ScreenerExchangeData,
  ScreenerDiscoveryFilters,
  ScreenerDiscoveryChangePeriod,
  ScreenerDiscoveryResponse,
  DebugSnapshot,
  PositionHistoryData,
  TickerInfo,
  AnalyzeResult,
  AnalyzeStatusResponse,
  AnalyzeTradeFlow,
  ExchangeOptionsResponse,
  TradePatternSettings,
  TelegramAccount,
} from '@/types'

let csrfToken: string | null = null
const DEFAULT_FETCH_TIMEOUT_MS = 30000

export function setCsrfToken(token: string) {
  csrfToken = token
}

type ApiOkResponse = {
  ok: boolean
  error?: string
}

type FetchWithAuthOptions = RequestInit & {
  redirectOnUnauthorized?: boolean
  timeoutMs?: number
}

function getRouterBasename(): string {
  const basename = import.meta.env.VITE_ROUTER_BASENAME || ''
  if (!basename || basename === '/') return ''
  const normalized = basename.startsWith('/') ? basename : `/${basename}`
  return normalized.replace(/\/+$/, '')
}

function getPathWithinRouter(pathname: string): string {
  const basename = getRouterBasename()
  if (!basename) return pathname
  if (pathname === basename) return '/'
  if (pathname.startsWith(`${basename}/`)) return pathname.slice(basename.length) || '/'
  return pathname
}

function isLoginPath(pathname: string): boolean {
  return getPathWithinRouter(pathname) === '/login'
}

export async function parseJsonResponse<T = any>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text.trim()) {
    return {} as T
  }

  try {
    return JSON.parse(text) as T
  } catch {
    const contentType = response.headers.get('content-type') || ''
    const trimmed = text.trimStart().toLowerCase()
    const receivedHtml = contentType.includes('text/html') ||
      trimmed.startsWith('<!doctype') ||
      trimmed.startsWith('<html')

    if (receivedHtml) {
      throw new Error(
        `API returned HTML instead of JSON (${response.status}). Check that the FastAPI backend is running and that /api routes are proxied to it.`
      )
    }

    throw new Error(`API returned invalid JSON (${response.status}).`)
  }
}

async function readOkResponse(response: Response): Promise<ApiOkResponse> {
  let data: { ok?: boolean; error?: string; detail?: unknown } = {}
  try {
    data = await parseJsonResponse(response)
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Invalid API response',
    }
  }

  const detail = typeof data.detail === 'string' ? data.detail : undefined
  return {
    ok: response.ok && data.ok !== false,
    error: data.error || detail,
  }
}

async function readOkOrThrow(response: Response, fallbackError: string): Promise<ApiOkResponse> {
  const result = await readOkResponse(response)
  if (!result.ok) {
    throw new Error(result.error || fallbackError)
  }
  return result
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_FETCH_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
  const parentSignal = init.signal
  let removeAbortListener: (() => void) | undefined

  if (parentSignal) {
    if (parentSignal.aborted) {
      controller.abort()
    } else {
      const abort = () => controller.abort()
      parentSignal.addEventListener('abort', abort, { once: true })
      removeAbortListener = () => parentSignal.removeEventListener('abort', abort)
    }
  }

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    })
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError' && !parentSignal?.aborted) {
      throw new Error('Request timed out')
    }
    throw err
  } finally {
    window.clearTimeout(timeoutId)
    removeAbortListener?.()
  }
}

export async function fetchWithAuth(url: string, options: FetchWithAuthOptions = {}): Promise<Response> {
  const { redirectOnUnauthorized = true, timeoutMs, ...fetchOptions } = options
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  }

  // Add CSRF token for mutating requests
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(fetchOptions.method || '')) {
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }
  }

  const response = await fetchWithTimeout(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  }, timeoutMs)

  // Handle 401 by redirecting to login
  if (response.status === 401) {
    if (redirectOnUnauthorized) {
      const currentPath = window.location.pathname
      if (!isLoginPath(currentPath)) {
        const loginPath = `${getRouterBasename()}/login`
        window.location.href = `${loginPath}?redirect=${encodeURIComponent(currentPath)}`
      }
      throw new Error('Unauthorized')
    }
    return response
  }

  // Check for other errors (clone response to preserve body for caller)
  if (!response.ok) {
    const responseClone = response.clone()
    const errorText = await responseClone.text()
    console.error(`API error ${response.status}: ${url}`, errorText)
  }

  return response
}

// Auth API
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetchWithAuth('/api/auth/me', {
      redirectOnUnauthorized: false,
      cache: 'no-store',
    })
    if (!response.ok) return null
    const data = await parseJsonResponse(response)
    if (data.csrf_token) {
      setCsrfToken(data.csrf_token)
    }
    return data
  } catch {
    return null
  }
}

export async function logout(): Promise<void> {
  await fetchWithAuth('/api/auth/logout', { method: 'POST' })
  window.location.href = '/login'
}

export async function changeAccountPassword(payload: {
  current_password: string
  new_password: string
}): Promise<ApiOkResponse> {
  const response = await fetchWithAuth('/api/account/password', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readOkResponse(response)
}

export async function updateAccountPushover(payload: { user_key: string }): Promise<ApiOkResponse> {
  const response = await fetchWithAuth('/api/account/pushover', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readOkResponse(response)
}

export async function updateAccountTelegramAliases(aliases: string[]): Promise<ApiOkResponse> {
  const response = await fetchWithAuth('/api/account/telegram/aliases', {
    method: 'POST',
    body: JSON.stringify({ aliases }),
  })
  return readOkResponse(response)
}

export async function unlinkAccountTelegram(userId: string): Promise<ApiOkResponse> {
  const response = await fetchWithAuth(`/api/account/telegram/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })
  return readOkResponse(response)
}

export async function updateAccountPreferences(preferences: UserPreferences): Promise<ApiOkResponse> {
  const response = await fetchWithAuth('/api/account/preferences', {
    method: 'POST',
    body: JSON.stringify({ preferences }),
  })
  return readOkResponse(response)
}

// Exchange Options API
export async function getExchangeOptions(): Promise<ExchangeOptionsResponse> {
  const response = await fetchWithAuth('/api/exchange-options')
  return parseJsonResponse(response)
}

// OI Alerts API
export async function getOIAlertsStatus(): Promise<OIAlertsStatus> {
  const response = await fetchWithAuth('/api/alerts/status')
  return parseJsonResponse(response)
}

export async function getOIAlertsTriggers(): Promise<{ items: AlertTrigger[] }> {
  const response = await fetchWithAuth('/api/alerts/triggers')
  return parseJsonResponse(response)
}

export async function getOIAlertsSnapshot(): Promise<AlertSnapshot> {
  const response = await fetchWithAuth('/api/alerts/snapshot')
  return parseJsonResponse(response)
}

export async function startOIAlerts(): Promise<void> {
  await fetchWithAuth('/api/alerts/start', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function stopOIAlerts(): Promise<void> {
  await fetchWithAuth('/api/alerts/stop', { method: 'POST' })
}

export async function saveOIAlert(alert: Partial<Alert>): Promise<{ ok: boolean; alerts: Alert[] }> {
  const response = await fetchWithAuth('/api/alerts/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ alert }),
  })
  return parseJsonResponse(response)
}

export async function deleteOIAlert(id: string): Promise<{ ok: boolean; alerts: Alert[] }> {
  const response = await fetchWithAuth(`/api/alerts/settings/alert/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

// Orderbook Alert API
export async function saveOrderbookAlert(alert: Partial<OrderbookAlert>): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/orderbook/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ alert }),
  })
  return parseJsonResponse(response)
}

export async function deleteOrderbookAlert(id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/orderbook/settings/alert/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

// Mark Price Alert API
export async function saveMarkPriceAlert(alert: Partial<MarkPriceAlert>): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/mark-price/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ alert }),
  })
  return parseJsonResponse(response)
}

export async function deleteMarkPriceAlert(id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/mark-price/settings/alert/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

// Price Difference Alert API
export async function savePriceDifferenceAlert(alert: Partial<PriceDifferenceAlert>): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/price-difference/settings/alert', {
    method: 'POST',
    body: JSON.stringify(alert),
  })
  return parseJsonResponse(response)
}

export async function deletePriceDifferenceAlert(id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/price-difference/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ _delete_id: id }),
  })
  return parseJsonResponse(response)
}

// Funding Alert API
export async function getFundingStatus(): Promise<FundingStatus> {
  const response = await fetchWithAuth('/api/funding/status')
  return parseJsonResponse(response)
}

export async function getFundingTriggers(): Promise<{ items: [] }> {
  const response = await fetchWithAuth('/api/funding/triggers')
  return parseJsonResponse(response)
}

export async function saveFundingAlert(alert: Partial<FundingAlert>): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/funding/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ alert }),
  })
  return parseJsonResponse(response)
}

export async function deleteFundingAlert(id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/funding/settings/alert/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

export async function startFunding(): Promise<void> {
  await fetchWithAuth('/api/funding/start', { method: 'POST' })
}

export async function stopFunding(): Promise<void> {
  await fetchWithAuth('/api/funding/stop', { method: 'POST' })
}

// Orderbook API
export async function getOrderbookStatus(): Promise<OrderbookStatus> {
  const response = await fetchWithAuth('/api/orderbook/status')
  return parseJsonResponse(response)
}

export async function getOrderbookTracked(): Promise<{ orders: TrackedOrder[] }> {
  const response = await fetchWithAuth('/api/orderbook/tracked')
  return parseJsonResponse(response)
}

export async function startOrderbook(): Promise<void> {
  await fetchWithAuth('/api/orderbook/start', { method: 'POST' })
}

export async function stopOrderbook(): Promise<void> {
  await fetchWithAuth('/api/orderbook/stop', { method: 'POST' })
}

// Mark Price API
export async function getMarkPriceStatus(): Promise<MarkPriceStatus> {
  const response = await fetchWithAuth('/api/mark-price/status')
  return parseJsonResponse(response)
}

export async function getMarkPriceTriggers(): Promise<{ items: MarkPriceTrigger[] }> {
  const response = await fetchWithAuth('/api/mark-price/triggers')
  return parseJsonResponse(response)
}

export async function startMarkPrice(): Promise<void> {
  await fetchWithAuth('/api/mark-price/start', { method: 'POST' })
}

export async function stopMarkPrice(): Promise<void> {
  await fetchWithAuth('/api/mark-price/stop', { method: 'POST' })
}

// Price Difference API
export async function getPriceDifferenceStatus(): Promise<PriceDifferenceStatus> {
  const response = await fetchWithAuth('/api/price-difference/status')
  return parseJsonResponse(response)
}

export async function getPriceDifferenceSettings(): Promise<PriceDifferenceSettings> {
  const response = await fetchWithAuth('/api/price-difference/settings')
  return parseJsonResponse(response)
}

export async function startPriceDifference(): Promise<void> {
  await fetchWithAuth('/api/price-difference/start', { method: 'POST' })
}

export async function stopPriceDifference(): Promise<void> {
  await fetchWithAuth('/api/price-difference/stop', { method: 'POST' })
}

// Splash Alert API
export async function getSplashStatus(): Promise<SplashStatus> {
  const response = await fetchWithAuth('/api/splash/status')
  return parseJsonResponse(response)
}

export async function getSplashSettings(): Promise<SplashSettings> {
  const response = await fetchWithAuth('/api/splash/settings')
  return parseJsonResponse(response)
}

export async function saveSplashAlert(alert: Partial<SplashAlert>): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/splash/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ alert }),
  })
  return parseJsonResponse(response)
}

export async function deleteSplashAlert(id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/splash/settings/alert/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

export async function updateSplashAlertsIgnore(ignore: string[]): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/splash/settings/ignore', {
    method: 'POST',
    body: JSON.stringify({ ignore }),
  })
  return parseJsonResponse(response)
}

export async function startSplash(): Promise<void> {
  await fetchWithAuth('/api/splash/start', { method: 'POST' })
}

export async function stopSplash(): Promise<void> {
  await fetchWithAuth('/api/splash/stop', { method: 'POST' })
}

// Spread Alert API
export async function getSpreadStatus(): Promise<SpreadStatus> {
  const response = await fetchWithAuth('/api/spread/status')
  return parseJsonResponse(response)
}

export async function getSpreadSettings(): Promise<SpreadSettings> {
  const response = await fetchWithAuth('/api/spread/settings')
  return parseJsonResponse(response)
}

export async function saveSpreadAlert(alert: Partial<SpreadAlert>): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/spread/settings/alert', {
    method: 'POST',
    body: JSON.stringify({ alert }),
  })
  return parseJsonResponse(response)
}

export async function deleteSpreadAlert(id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/spread/settings/alert/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

export async function updateSpreadAlertsIgnore(ignore: string[]): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/spread/settings/ignore', {
    method: 'POST',
    body: JSON.stringify({ ignore }),
  })
  return parseJsonResponse(response)
}

export async function startSpread(): Promise<void> {
  await fetchWithAuth('/api/spread/start', { method: 'POST' })
}

export async function stopSpread(): Promise<void> {
  await fetchWithAuth('/api/spread/stop', { method: 'POST' })
}

// Tags API
async function parseTagResponse<T>(response: Response): Promise<T> {
  const data = await parseJsonResponse<T & { detail?: string; error?: string }>(response)
  if (!response.ok) {
    throw new Error(data.detail || data.error || `Tag request failed (${response.status})`)
  }
  return data
}

export async function getTags(): Promise<{ tags: Record<string, string[]> }> {
  const response = await fetchWithAuth('/api/tags')
  return parseJsonResponse(response)
}

export async function addTag(symbol: string, tag: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/tags/${encodeURIComponent(symbol)}`, {
    method: 'POST',
    body: JSON.stringify({ tag }),
  })
  return parseTagResponse(response)
}

export interface TemporaryTagResponse {
  ok: boolean
  symbol: string
  tag: string
  tags: string[]
  expires_at?: number
  expires_in_hours?: number
}

export async function addTemporaryTag(symbol: string, tag: string, hours: number = 24): Promise<TemporaryTagResponse> {
  const response = await fetchWithAuth(`/api/tags/${encodeURIComponent(symbol)}/temporary`, {
    method: 'POST',
    body: JSON.stringify({ tag, hours }),
  })
  return parseTagResponse(response)
}

export async function removeTag(symbol: string, tag: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/tags/${encodeURIComponent(symbol)}/${encodeURIComponent(tag)}`, {
    method: 'DELETE',
  })
  return parseTagResponse(response)
}

export async function addListingTag(symbol: string, hours: number = 24): Promise<{ ok: boolean; expires_at?: number; expires_in_hours?: number }> {
  const response = await fetchWithAuth(`/api/tags/${encodeURIComponent(symbol)}/listing`, {
    method: 'POST',
    body: JSON.stringify({ hours }),
  })
  return parseJsonResponse(response)
}

export interface TagWithExpiry {
  tag: string
  expires_at: number | null
}

export async function getTagsWithExpiry(): Promise<{ tags: Record<string, TagWithExpiry[]> }> {
  const response = await fetchWithAuth('/api/tags/with_expiry')
  return parseJsonResponse(response)
}

export interface TagSummary {
  tag: string
  coin_count: number
}

export async function getTagSummary(): Promise<{ tags: TagSummary[] }> {
  const response = await fetchWithAuth('/api/tags/summary')
  return parseTagResponse(response)
}

export async function getSymbolTagsWithExpiry(symbol: string): Promise<{ tags: TagWithExpiry[] }> {
  const response = await fetchWithAuth(`/api/tags/${encodeURIComponent(symbol)}/with_expiry`)
  return parseTagResponse(response)
}

export async function triggerTagCleanup(): Promise<{ ok: boolean; removed_count: number }> {
  const response = await fetchWithAuth('/api/tags/cleanup', { method: 'POST' })
  return parseTagResponse(response)
}


// Aliases API
export async function getAliases(): Promise<{ aliases: Aliases }> {
  const response = await fetchWithAuth('/api/aliases')
  if (!response.ok) await readOkOrThrow(response, 'Failed to load aliases')
  return parseJsonResponse(response)
}

export async function addAlias(
  groupName: string,
  exchange: string,
  exchangeSymbol: string
): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/aliases/${encodeURIComponent(groupName)}`, {
    method: 'POST',
    body: JSON.stringify({ exchange, exchange_symbol: exchangeSymbol }),
  })
  return readOkOrThrow(response, 'Failed to save alias')
}

export async function deleteAliasGroup(groupName: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(`/api/aliases/${encodeURIComponent(groupName)}`, {
    method: 'DELETE',
  })
  return readOkOrThrow(response, 'Failed to delete alias group')
}

// Notifications API
export async function sendCriticalNotification(message: string, title?: string): Promise<{ ok: boolean; error?: string }> {
  const response = await fetchWithAuth('/api/pushover/critical', {
    method: 'POST',
    body: JSON.stringify({ message, title: title || '🚨 CRITICAL ALERT' }),
  })
  return parseJsonResponse(response)
}

export async function sendWarningNotification(message: string, title?: string): Promise<{ ok: boolean; error?: string }> {
  const response = await fetchWithAuth('/api/pushover/warning', {
    method: 'POST',
    body: JSON.stringify({ message, title: title || '⚠️ WARNING' }),
  })
  return parseJsonResponse(response)
}

// Global Settings API
export type TelegramTopicConfig = string | number | null | {
  name?: string
  label?: string
  id?: string | number | null
  topic_id?: string | number | null
  message_thread_id?: string | number | null
  reply_to_message_id?: string | number | null
}

export interface TelegramChannelConfig {
  name?: string
  label?: string
  bot?: string
  chat_id?: string | number
  topics?: Record<string, TelegramTopicConfig>
}

export interface TelegramDestination {
  id: string
  channel_key: string
  channel_name: string
  topic_key?: string | null
  topic_name?: string | null
  label: string
  chat_id: string
  reply_to_message_id?: string | null
}

export interface TelegramSystemReportsSettings {
  enabled?: boolean
  chat_id?: string | number
  topic_id?: string | number
  health_digest_interval_seconds?: number
  min_log_level?: string
  include_log_tail_bytes?: number
}

export interface GlobalSettings {
  telegram: {
    bot_token_masked?: string
    login_client_id?: string
    login_client_secret_masked?: string
    login_redirect_uri?: string
    default_chat_id?: string
    default_reply_to_message_id?: string | number
    position_discussion_chat_id?: string
    webapp_url?: string
    info_chat?: string | number
    info_topic_id?: string | number
    history_feed_channel_id?: string | number
    history_feed_topic_id?: string | number
    system_reports?: TelegramSystemReportsSettings
  }
  notifiers?: {
    telegram?: {
      bots?: Record<string, { token?: string }>
      channels?: Record<string, TelegramChannelConfig>
    }
    pushover?: Record<string, unknown>
  }
  telegram_destinations?: TelegramDestination[]
  pushover: {
    user_key?: string
    app_token?: string
    group_key?: string
  }
  orderbook_settings: {
    default_update_notification_pct?: number
  }
  vertex_ai: {
    api_key_masked?: string
    model?: string
    project_id?: string
    location?: string
  }
  arkham: {
    enabled?: boolean
    api_key_masked?: string
    max_transfers?: number
    min_transfer_usd?: number
  }
  storage_box: {
    enabled?: boolean
    transport?: 'local' | 'webdav'
    local_path?: string
    webdav_url?: string
    username?: string
    password_masked?: string
    remote_path?: string
    crash_logs_path?: string
    timeout_seconds?: number
    verify_tls?: boolean
    crash_reports?: {
      enabled?: boolean
      local_spool_dir?: string
      include_recent_log_bytes?: number
    }
    database_backups?: {
      enabled?: boolean
      local_spool_dir?: string
      required_mountpoint?: string
      remote_path?: string
      interval_seconds?: number
      run_on_startup?: boolean
      retention_count?: number
      snapshot_mode?: 'auto' | 'compact' | 'online'
      compression?: 'zstd' | 'gzip' | 'none'
      compression_level?: number
      process_nice?: number
      cpu_limit_percent?: number
      io_priority?: 'idle' | 'best-effort' | 'unchanged'
      compress?: boolean
    }
  }
  oi_alerts_ignore: string[]
  orderbook_alerts_ignore: string[]
  mark_price_alerts_ignore: string[]
}

export async function getGlobalSettings(): Promise<GlobalSettings> {
  const response = await fetchWithAuth('/api/settings', { cache: 'no-store' })
  if (!response.ok) {
    throw new Error('Failed to load global settings')
  }
  return parseJsonResponse(response)
}

export interface TradePatternSettingsResponse {
  ok: boolean
  config: TradePatternSettings
  presets: Record<TradePatternSettings['profile'], TradePatternSettings>
}

export async function getTradePatternSettings(): Promise<TradePatternSettingsResponse> {
  const response = await fetchWithAuth('/api/settings/trade-patterns', { cache: 'no-store' })
  return parseJsonResponse(response)
}

export async function updateTradePatternSettings(
  profile: TradePatternSettings['profile'],
  config: TradePatternSettings
): Promise<{ ok: boolean; config: TradePatternSettings }> {
  const response = await fetchWithAuth('/api/settings/trade-patterns', {
    method: 'PUT',
    body: JSON.stringify({ profile, config }),
  })
  if (!response.ok) throw new Error('Failed to save trade-pattern settings')
  return parseJsonResponse(response)
}

export async function updateTelegramSettings(settings: {
  bot_token?: string
  login_client_id?: string
  login_client_secret?: string
  login_redirect_uri?: string
  default_chat_id?: string
  default_reply_to_message_id?: string | number
  position_discussion_chat_id?: string
  webapp_url?: string
  info_chat?: string | number
  info_topic_id?: string | number
  history_feed_channel_id?: string | number
  history_feed_topic_id?: string | number
  system_reports?: TelegramSystemReportsSettings
  channels?: Record<string, TelegramChannelConfig>
}): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/telegram', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return readOkOrThrow(response, 'Failed to save Telegram settings')
}

export async function updatePositionDiscussion(chat_id: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/position_discussion', {
    method: 'POST',
    body: JSON.stringify({ position_discussion_chat_id: chat_id }),
  })
  return readOkOrThrow(response, 'Failed to save position discussion settings')
}

export async function updatePushoverSettings(settings: {
  user_key?: string
  app_token?: string
  group_key?: string
}): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/pushover', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return readOkOrThrow(response, 'Failed to save Pushover settings')
}

export async function updateOrderbookDefaults(settings: {
  default_update_notification_pct?: number | null
}): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/orderbook_defaults', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return readOkOrThrow(response, 'Failed to save orderbook defaults')
}

export async function updateVertexAISettings(settings: {
  api_key?: string
  model?: string
  project_id?: string
  location?: string
}): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/vertex_ai', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return readOkOrThrow(response, 'Failed to save Gemini settings')
}

export async function updateArkhamSettings(settings: {
  api_key?: string
  enabled?: boolean
  max_transfers?: number
  min_transfer_usd?: number
}): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/arkham', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return readOkOrThrow(response, 'Failed to save Arkham settings')
}

export async function updateStorageBoxSettings(settings: {
  enabled?: boolean
  transport?: 'local' | 'webdav'
  local_path?: string
  webdav_url?: string
  username?: string
  password?: string
  clear_password?: boolean
  remote_path?: string
  crash_logs_path?: string
  timeout_seconds?: number
  verify_tls?: boolean
  crash_reports?: {
    enabled?: boolean
    local_spool_dir?: string
    include_recent_log_bytes?: number
  }
  database_backups?: {
    enabled?: boolean
    local_spool_dir?: string
    required_mountpoint?: string
    remote_path?: string
    interval_seconds?: number
    run_on_startup?: boolean
    retention_count?: number
    snapshot_mode?: 'auto' | 'compact' | 'online'
    compression?: 'zstd' | 'gzip' | 'none'
    compression_level?: number
    process_nice?: number
    cpu_limit_percent?: number
    io_priority?: 'idle' | 'best-effort' | 'unchanged'
    compress?: boolean
  }
}): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/storage_box', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return readOkOrThrow(response, 'Failed to save Storage Box settings')
}

// State tools API (Admin)
export async function getStateStatus(): Promise<StateStatusResponse> {
  const response = await fetchWithAuth('/api/state/status')
  return parseJsonResponse(response)
}

export async function clearState(payload: {
  all?: boolean
  components?: string[]
}): Promise<StateClearResponse> {
  const response = await fetchWithAuth('/api/state/clear', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return parseJsonResponse(response)
}

export async function updateOIAlertsIgnore(ignore: string[]): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/oi_alerts_ignore', {
    method: 'POST',
    body: JSON.stringify({ ignore }),
  })
  return parseJsonResponse(response)
}

export async function updateOrderbookAlertsIgnore(ignore: string[]): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/orderbook_alerts_ignore', {
    method: 'POST',
    body: JSON.stringify({ ignore }),
  })
  return parseJsonResponse(response)
}

export async function updateMarkPriceAlertsIgnore(ignore: string[]): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/settings/mark_price_alerts_ignore', {
    method: 'POST',
    body: JSON.stringify({ ignore }),
  })
  return parseJsonResponse(response)
}

export async function updatePriceDifferenceAlertsIgnore(ignore: string[]): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth('/api/price-difference/settings/ignore', {
    method: 'POST',
    body: JSON.stringify({ ignore }),
  })
  return parseJsonResponse(response)
}

export type AlertType = 'oi' | 'orderbook' | 'mark_price' | 'price_diff' | 'funding' | 'splash' | 'spread'

export async function addToIgnoreList(alertType: AlertType, symbol: string): Promise<{ ok: boolean; symbol?: string; ignore?: string[] }> {
  const response = await fetchWithAuth(`/api/settings/ignore/${alertType}/add`, {
    method: 'POST',
    body: JSON.stringify({ symbol }),
  })
  return parseJsonResponse(response)
}

// Get users available for notifications
export interface NotificationUser {
  id: string
  username: string
  pushover_user_key?: string
  telegram_nickname?: string
  telegram_nicknames?: string[]
  telegram_chat_id?: string
  telegram_chat_ids?: string[]
}

export async function getNotificationUsers(): Promise<{ users: NotificationUser[] }> {
  const response = await fetchWithAuth('/api/settings/users')
  return parseJsonResponse(response)
}

// User Management API (Admin only)
export interface AuthUser {
  username: string
  role: 'admin' | 'user'
  active: boolean
  permissions: Record<string, boolean>
  pushover_user_key?: string
  telegram_nickname?: string
  telegram_nicknames?: string[]
  telegram_chat_id?: string
  telegram_chat_ids?: string[]
  telegram_accounts?: TelegramAccount[]
  has_password?: boolean
  availability_status?: 'online' | 'offline'
  availability_changed_at?: string | null
}

export async function getAuthUsers(): Promise<{ users: AuthUser[] }> {
  const response = await fetchWithAuth('/api/auth/users')
  return parseJsonResponse(response)
}

export async function createAuthUser(user: {
  username: string
  password: string
  role: 'admin' | 'user'
  active?: boolean
  permissions?: Record<string, boolean>
  pushover_user_key?: string
  telegram_nickname?: string
  telegram_nicknames?: string[]
  telegram_chat_id?: string
  telegram_chat_ids?: string[]
}): Promise<{ ok: boolean; user?: AuthUser; error?: string }> {
  const response = await fetchWithAuth('/api/auth/users', {
    method: 'POST',
    body: JSON.stringify(user),
  })
  return parseJsonResponse(response)
}

export async function updateAuthUser(
  username: string,
  updates: {
    password?: string
    role?: 'admin' | 'user'
    active?: boolean
    permissions?: Record<string, boolean>
    pushover_user_key?: string
    telegram_nickname?: string
    telegram_nicknames?: string[]
    telegram_chat_id?: string
    telegram_chat_ids?: string[]
  }
): Promise<{ ok: boolean; user?: AuthUser; error?: string }> {
  const response = await fetchWithAuth(`/api/auth/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
  return parseJsonResponse(response)
}

export async function deleteAuthUser(username: string): Promise<{ ok: boolean; error?: string }> {
  const response = await fetchWithAuth(`/api/auth/users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
  })
  return parseJsonResponse(response)
}

export async function unlinkAuthUserTelegram(
  username: string,
  userId: string
): Promise<{ ok: boolean; user?: AuthUser; error?: string }> {
  const response = await fetchWithAuth(
    `/api/auth/users/${encodeURIComponent(username)}/telegram/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  )
  return parseJsonResponse(response)
}

export async function mergeAuthUserTelegram(sourceUsername: string, targetUsername: string): Promise<AuthUser> {
  const response = await fetchWithAuth(
    `/api/auth/users/${encodeURIComponent(sourceUsername)}/merge-telegram`,
    { method: 'POST', body: JSON.stringify({ target_username: targetUsername }) }
  )
  const data = await parseJsonResponse<{ ok: boolean; user?: AuthUser; error?: string; detail?: string }>(response)
  if (!response.ok || !data.ok || !data.user) {
    throw new Error(data.error || data.detail || 'Failed to merge Telegram user')
  }
  return data.user
}

// Send notification to specific users
export async function sendNotificationToUsers(
  userIds: string[],
  message: string,
  title?: string,
  urgency: number = 0
): Promise<{ ok: boolean; error?: string }> {
  const response = await fetchWithAuth('/api/pushover/users', {
    method: 'POST',
    body: JSON.stringify({ user_ids: userIds, message, title, urgency }),
  })
  return parseJsonResponse(response)
}

// Position Discussion API
export interface LivePosition {
  id: string
  symbol: string
  direction: string | null
  entry_price: number | null
  current_price: number | null
  pnl_pct: number | null
  created_by: string
  created_at: string
  duration: string
  users_in_position: string[]
  decision_reason: string
  topic_id: number | null
}

interface PositionMutationResponse {
  ok: boolean
  position?: Position
  users_added?: string[]
  users_removed?: string[]
  error?: string
}

async function readPositionMutationResponse(
  response: Response,
  fallbackError: string,
): Promise<PositionMutationResponse> {
  const data = await parseJsonResponse<{
    ok?: boolean
    position?: Position
    users_added?: string[]
    users_removed?: string[]
    error?: string
    detail?: unknown
  }>(response)
  const detail = typeof data.detail === 'string' ? data.detail : undefined

  return {
    ...data,
    ok: response.ok && data.ok !== false,
    error: data.error || detail || (!response.ok ? `${fallbackError} (HTTP ${response.status})` : undefined),
  }
}

export async function getPositions(status?: 'open' | 'closed'): Promise<{ positions: Position[] }> {
  const url = status ? `/api/positions?status=${status}` : '/api/positions'
  const response = await fetchWithAuth(url)
  return parseJsonResponse(response)
}

export async function getLivePositions(): Promise<{ positions: LivePosition[] }> {
  const response = await fetchWithAuth('/api/positions/live')
  return parseJsonResponse(response)
}

export async function getPosition(positionId: string): Promise<{ position: Position }> {
  const response = await fetchWithAuth(`/api/positions/${encodeURIComponent(positionId)}`)
  return parseJsonResponse(response)
}

export async function createPosition(payload: CreatePositionPayload): Promise<PositionMutationResponse> {
  const response = await fetchWithAuth('/api/positions/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readPositionMutationResponse(response, 'Failed to create discussion')
}

export async function closePosition(positionId: string, closedReason: string, closedResult: 'profit' | 'loss'): Promise<PositionMutationResponse> {
  const response = await fetchWithAuth(`/api/positions/${encodeURIComponent(positionId)}/close`, {
    method: 'POST',
    body: JSON.stringify({ closed_reason: closedReason, closed_result: closedResult }),
  })
  return readPositionMutationResponse(response, 'Failed to close discussion')
}

export async function updatePosition(positionId: string, payload: UpdatePositionPayload): Promise<PositionMutationResponse> {
  const response = await fetchWithAuth(`/api/positions/${encodeURIComponent(positionId)}/update`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readPositionMutationResponse(response, 'Failed to update discussion')
}

export async function reopenPosition(positionId: string, payload: ReopenPositionPayload = {}): Promise<PositionMutationResponse> {
  const response = await fetchWithAuth(`/api/positions/${encodeURIComponent(positionId)}/reopen`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return readPositionMutationResponse(response, 'Failed to reopen discussion')
}

export async function getPositionUsers(): Promise<{ users: PositionUser[] }> {
  const response = await fetchWithAuth('/api/settings/users')
  const data = await parseJsonResponse(response)
  // Map settings users to PositionUser format (add 'name' field)
  const users = (data.users || []).map((u: NotificationUser) => ({
    ...u,
    name: u.telegram_nicknames?.[0] || u.telegram_nickname || u.username,
  }))
  return { users }
}

export async function sendPositionNotification(positionId: string, message: string, urgency: number = 0): Promise<{ ok: boolean; error?: string }> {
  const response = await fetchWithAuth(`/api/positions/${encodeURIComponent(positionId)}/notify`, {
    method: 'POST',
    body: JSON.stringify({ message, urgency }),
  })
  return parseJsonResponse(response)
}

// Helper to safely fetch with fallback
async function safeFetch<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn()
  } catch (e) {
    console.warn('API call failed:', e)
    return fallback
  }
}

// Overview/Dashboard API
export async function getAlertsOverview(): Promise<{
  oi: { triggers: AlertTrigger[]; running: boolean; alertsCount: number }
  orderbook: { tracked: number; notified: number; running: boolean; alertsCount: number }
  mark_price: { triggers: number; running: boolean; alertsCount: number }
  price_diff: { running: boolean; alertsCount: number }
}> {
  const [oiStatus, oiTriggers, orderbookStatus, markPriceStatus, priceDiffStatus, priceDiffSettings] = await Promise.all([
    safeFetch(getOIAlertsStatus, { running: true, alerts: [], ignore: [] } as OIAlertsStatus),
    safeFetch(getOIAlertsTriggers, { items: [] }),
    safeFetch(getOrderbookStatus, { running: true, orderbook_alerts: [] } as OrderbookStatus),
    safeFetch(getMarkPriceStatus, { running: true, mark_price_alerts: [] } as MarkPriceStatus),
    safeFetch(getPriceDifferenceStatus, { status: 'active' } as PriceDifferenceStatus),
    safeFetch(getPriceDifferenceSettings, { price_difference_alerts: [] } as PriceDifferenceSettings),
  ])

  return {
    oi: {
      triggers: oiTriggers.items || [],
      running: Boolean(oiStatus.running),
      alertsCount: oiStatus.alerts?.length || 0,
    },
    orderbook: {
      tracked: orderbookStatus.tracked_count || 0,
      notified: orderbookStatus.notified_count || 0,
      running: Boolean(orderbookStatus.running),
      alertsCount: orderbookStatus.orderbook_alerts?.length || 0,
    },
    mark_price: {
      triggers: markPriceStatus.triggers_count || 0,
      running: Boolean(markPriceStatus.running),
      alertsCount: markPriceStatus.mark_price_alerts?.length || 0,
    },
    price_diff: {
      running: priceDiffStatus.status === 'active',
      alertsCount: priceDiffSettings.price_difference_alerts?.length || 0,
    },
  }
}

// Full Alerts Overview API - calls /api/alerts/overview with all filters
export async function getAlertsOverviewFull(params: {
  exchange?: string
  alert_type?: string
  tags?: string
  time_range?: string
  alert_age?: string
  alert_names?: string
  market_type?: string
  min_value?: number
  max_value?: number
} = {}): Promise<{
  alerts: Array<Record<string, unknown>>
  summary: {
    total_count: number
    by_type: Record<string, number>
    by_exchange: Record<string, number>
    by_symbol: Record<string, number>
  }
  alert_names: string[]
  unique_tags: string[]
}> {
  const queryParams = new URLSearchParams()

  if (params.exchange) queryParams.set('exchange', params.exchange)
  if (params.alert_type) queryParams.set('alert_type', params.alert_type)
  if (params.tags) queryParams.set('tags', params.tags)
  if (params.time_range) queryParams.set('time_range', params.time_range)
  if (params.alert_age) queryParams.set('alert_age', params.alert_age)
  if (params.alert_names) queryParams.set('alert_names', params.alert_names)
  if (params.market_type) queryParams.set('market_type', params.market_type)
  if (params.min_value !== undefined) queryParams.set('min_value', String(params.min_value))
  if (params.max_value !== undefined) queryParams.set('max_value', String(params.max_value))

  const queryString = queryParams.toString()
  const url = queryString ? `/api/alerts/overview?${queryString}` : '/api/alerts/overview'

  const response = await fetchWithAuth(url)
  return parseJsonResponse(response)
}

// Coin Market Data API
export async function fetchCoinMarketData(
  sessionId: string,
  symbols: string[],
  period: string = '1m',
  includeSpot: boolean = true,
  includeFutures: boolean = true,
  exchanges: string[] = [],
  signal?: AbortSignal,
): Promise<{ items: MarketRecord[] }> {
  const queryParams = new URLSearchParams()
  queryParams.set('sid', sessionId)
  queryParams.set('symbols', symbols.join(','))
  queryParams.set('period', period)
  queryParams.set('spot', String(includeSpot))
  queryParams.set('futures', String(includeFutures))
  if (exchanges.length > 0) {
    queryParams.set('exchanges_param', exchanges.join(','))
  }

  const response = await fetchWithAuth(`/api/cp/data?${queryParams.toString()}`, { signal })
  return parseJsonResponse(response)
}

// Screener API
export async function getScreenerTickers(signal?: AbortSignal): Promise<{ tickers: ScreenerTicker[]; settings: ScreenerSettings }> {
  const response = await fetchWithAuth('/api/screener/tickers', { signal })
  try {
    const result = await parseJsonResponse<{ tickers: ScreenerTicker[]; settings: ScreenerSettings }>(response)
    if (!response.ok) throw new Error(`Failed to load tickers (${response.status}).`)
    return result
  } catch (error) {
    if (error instanceof Error) Object.assign(error, { status: response.status })
    throw error
  }
}

export async function addScreenerTicker(
  symbol: string,
  exchanges?: string[]
): Promise<{ ok: boolean; symbol?: string; available_exchanges?: string[]; error?: string }> {
  const response = await fetchWithAuth('/api/screener/add', {
    method: 'POST',
    body: JSON.stringify({ symbol, exchanges }),
  })
  return parseJsonResponse(response)
}

export async function removeScreenerTicker(symbol: string): Promise<{ ok: boolean; symbol?: string; error?: string }> {
  const response = await fetchWithAuth('/api/screener/remove', {
    method: 'POST',
    body: JSON.stringify({ symbol }),
  })
  return parseJsonResponse(response)
}

export async function getScreenerData(
  symbol: string,
  timeframe?: { mode: string; relative_seconds?: number; from_ts?: number; to_ts?: number },
  interval?: string,
  includeOrderbook = false,
  signal?: AbortSignal,
  oiExchanges?: string[],
  window?: { from: number; to: number },
  includeOIOhlc = false,
): Promise<{ ok: boolean; data?: ScreenerTickerData; error?: string }> {
  const params = new URLSearchParams()
  params.set('oi_include_ohlc', String(includeOIOhlc))
  if (interval) params.set('interval', interval)
  if (window) {
    params.set('window_from', String(window.from))
    params.set('window_to', String(window.to))
    params.set('limit', '5000')
  }
  if (oiExchanges !== undefined) params.set('oi_exchanges', oiExchanges.join(','))
  params.set('include_orderbook', includeOrderbook ? 'true' : 'false')
  // Make the view's mode explicit. Falling back to server-side per-user
  // settings lets another browser tab change the displayed time range.
  // New/unhydrated views are live by default.
  params.set('tf_mode', timeframe?.mode || 'live')
  if (timeframe && timeframe.mode !== 'live') {
    if (timeframe.mode === 'relative' && timeframe.relative_seconds) {
      params.set('tf_relative_seconds', String(timeframe.relative_seconds))
    }
    if (timeframe.mode === 'absolute') {
      if (timeframe.from_ts) params.set('tf_from', String(timeframe.from_ts))
      if (timeframe.to_ts) params.set('tf_to', String(timeframe.to_ts))
    }
  }
  const qs = params.toString()
  const url = `/api/screener/data/${encodeURIComponent(symbol)}${qs ? `?${qs}` : ''}`
  const response = await fetchWithAuth(url, { signal })
  return parseJsonResponse(response)
}

export async function getScreenerOIData(
  symbol: string,
  interval = '5s',
  signal?: AbortSignal,
  exchanges?: string[],
  includeOIOhlc = false,
): Promise<{
  ok: boolean
  data?: Pick<ScreenerTickerData, 'symbol' | 'query_symbol' | 'updated_at' | 'oi_history_until' | 'oi_interval' | 'live_oi'> & {
    exchanges: Record<string, Partial<ScreenerExchangeData>>
  }
  error?: string
}> {
  const params = new URLSearchParams({ interval, limit: '3' })
  params.set('oi_include_ohlc', String(includeOIOhlc))
  if (exchanges !== undefined) params.set('exchanges', exchanges.join(','))
  const response = await fetchWithAuth(
    `/api/screener/oi/${encodeURIComponent(symbol)}?${params.toString()}`,
    { signal, timeoutMs: 5000 },
  )
  return parseJsonResponse(response)
}

export async function getScreenerDiscovery(
  filters: ScreenerDiscoveryFilters,
): Promise<ScreenerDiscoveryResponse> {
  const params = new URLSearchParams()
  if (filters.exchanges.length > 0) params.set('exchanges', filters.exchanges.join(','))
  if (filters.search.trim()) params.set('search', filters.search.trim())
  if (filters.minOiUsd > 0) params.set('min_oi_usd', String(filters.minOiUsd))
  if (filters.minAbsChangePct > 0) params.set('min_abs_change_pct', String(filters.minAbsChangePct))
  if (filters.minAbsChangeUsd > 0) params.set('min_abs_change_usd', String(filters.minAbsChangeUsd))
  params.set('filter_scope', filters.filterScope)
  params.set('change_period', filters.changePeriod ?? '24h')
  params.set('change_direction', filters.changeDirection)
  params.set('min_matching_exchanges', String(filters.minMatchingExchanges))
  if (filters.includeTags.length > 0) params.set('include_tags', filters.includeTags.join(','))
  if (filters.excludeTags.length > 0) params.set('exclude_tags', filters.excludeTags.join(','))
  params.set('sort_by', filters.sortBy)
  params.set('sort_direction', filters.sortDirection)
  params.set('limit', '500')
  const response = await fetchWithAuth(`/api/screener/discovery?${params.toString()}`)
  return parseJsonResponse(response)
}

export async function getScreenerDiscoverySnapshot(
  exchanges: string[],
  signal?: AbortSignal,
  changePeriod: ScreenerDiscoveryChangePeriod = '24h',
): Promise<ScreenerDiscoveryResponse> {
  const params = new URLSearchParams({ exchanges: exchanges.join(','), snapshot: '1', change_period: changePeriod })
  const response = await fetchWithAuth(`/api/screener/discovery?${params.toString()}`, { signal })
  return parseJsonResponse(response)
}

export async function getScreenerSettings(): Promise<{ settings: ScreenerSettings }> {
  const response = await fetchWithAuth('/api/screener/settings')
  return parseJsonResponse(response)
}

export async function updateScreenerSettings(settings: Partial<ScreenerSettings>): Promise<{ ok: boolean; settings?: ScreenerSettings; error?: string }> {
  const response = await fetchWithAuth('/api/screener/settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  })
  return parseJsonResponse(response)
}

// Admin Debug API
export async function getAdminDebugSnapshot(): Promise<DebugSnapshot> {
  const response = await fetchWithAuth('/api/admin/debug/snapshot', {
    method: 'POST',
  })
  return parseJsonResponse(response)
}

export async function flushAdminDatabase(): Promise<{ ok: boolean; flushed_tables?: string[]; error?: string }> {
  const response = await fetchWithAuth('/api/admin/db/flush', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return parseJsonResponse(response)
}

// DB Browser API (Admin)
export interface DbTable {
  name: string
  row_count: number
}

export interface DbTableData {
  table: string
  columns: string[]
  types: string[]
  rows: unknown[][]
  total_rows: number
  page: number
  page_size: number
}

export interface DbTableRowCount {
  table: string
  row_count: number
}

export interface DbQueryResult {
  columns: string[]
  rows: unknown[][]
  row_count: number
  execution_time_ms: number
  error?: string
}

export async function getDbTables(): Promise<{ tables: DbTable[] }> {
  const response = await fetchWithAuth('/api/db/tables')
  return parseJsonResponse(response)
}

export async function getDbTableData(
  tableName: string,
  page: number = 1,
  pageSize: number = 100
): Promise<DbTableData> {
  const response = await fetchWithAuth(
    `/api/db/table/${encodeURIComponent(tableName)}?page=${page}&page_size=${pageSize}`
  )
  return parseJsonResponse(response)
}

export async function getDbTableRowCount(tableName: string): Promise<DbTableRowCount> {
  const response = await fetchWithAuth(
    `/api/db/table/${encodeURIComponent(tableName)}/count`,
    { timeoutMs: 120000 }
  )
  const data = await parseJsonResponse<DbTableRowCount & { error?: string }>(response)
  if (!response.ok) {
    throw new Error(data.error || `Failed to count rows in ${tableName}`)
  }
  return data
}

export async function executeDbQuery(sql: string, limit: number = 500): Promise<DbQueryResult> {
  const response = await fetchWithAuth('/api/db/query', {
    method: 'POST',
    body: JSON.stringify({ sql, limit }),
  })
  return parseJsonResponse(response)
}

// Coin Overview — History, Info, Analyze API

export async function getCoinHistory(ticker: string): Promise<PositionHistoryData> {
  const response = await fetchWithAuth(`/api/coin/${encodeURIComponent(ticker)}/history`)
  return parseJsonResponse(response)
}

export async function getCoinInfo(ticker: string): Promise<TickerInfo> {
  const response = await fetchWithAuth(`/api/ticker-info/${encodeURIComponent(ticker)}`)
  return parseJsonResponse(response)
}

export async function getCoinTrades(
  ticker: string,
  windowMinutes: number = 60,
  agoMinutes: number = 0
): Promise<AnalyzeTradeFlow> {
  const params = new URLSearchParams({
    window_minutes: String(windowMinutes),
    ago_minutes: String(agoMinutes),
  })
  const response = await fetchWithAuth(
    `/api/coin/${encodeURIComponent(ticker)}/trades?${params.toString()}`,
    { cache: 'no-store' }
  )
  return parseJsonResponse(response)
}

export interface LiveTradeSecond {
  timestamp: number
  buy_total_qty: number
  buy_total_notional: number
  buy_total_count: number
  buy_match_count: number
  sell_total_qty: number
  sell_total_notional: number
  sell_total_count: number
  sell_match_count: number
  quote_currency: string
  quantity_unit: string
  quality_flags: string[]
  persisted_at: number | null
  interval_elapsed: boolean
}

export interface LiveTradeSeconds {
  ticker: string
  as_of: number
  detail_available: boolean
  venues: {
    exchange: string
    market_type: string
    points: LiveTradeSecond[]
    unknown_minutes: number[]
  }[]
}

export async function getCoinTradeSeconds(ticker: string, signal?: AbortSignal): Promise<LiveTradeSeconds> {
  const response = await fetchWithAuth(
    `/api/coin/${encodeURIComponent(ticker)}/trades/seconds?window_seconds=10`,
    { cache: 'no-store', signal }
  )
  return parseJsonResponse(response)
}

export async function triggerCoinAnalyze(ticker: string): Promise<AnalyzeResult> {
  const response = await fetchWithAuth(`/api/coin/${encodeURIComponent(ticker)}/analyze`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return parseJsonResponse(response)
}

export async function getCoinAnalyzeStatus(ticker: string): Promise<AnalyzeStatusResponse> {
  const response = await fetchWithAuth(`/api/coin/${encodeURIComponent(ticker)}/analyze/status`)
  return parseJsonResponse(response)
}
